import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "../../hooks/use-toast";
import { UserX, UserCheck, Trash2, Save, Search, X, Mail, ShieldOff, ShieldCheck } from "lucide-react";

const STATUS_STYLES = {
  active: { bg: '#d6ecd6', color: '#154a1c', label: 'Active' },
  suspended: { bg: '#fde5c7', color: '#8a4a10', label: 'Suspended' },
  declined: { bg: '#f3d6d1', color: '#7a1f13', label: 'Declined' },
  deleted: { bg: '#e0e0e0', color: '#555', label: 'Deleted' },
};

const StatusPill = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.active;
  return <span className="px-2 py-1 rounded text-[11px] font-medium uppercase tracking-wider" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
};

const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleString() : "—"; } catch { return iso; } };

const EditModal = ({ member, onClose, onSave }) => {
  const [f, setF] = useState({
    display_name: member.display_name || "",
    first_name: member.first_name || "",
    last_name: member.last_name || "",
    email: member.email || "",
    phone: member.phone || "",
    headline: member.headline || "",
    company: member.company || "",
    industry: member.industry || "",
    bio: member.bio || "",
    location: member.location || "",
    linkedin: member.linkedin || "",
    searchable: !!member.searchable,
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(f); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(13,42,32,0.5)' }}>
      <form onSubmit={submit} className="rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{ background: '#fbf8ef' }} data-testid="member-edit-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[20px] font-medium">Edit member</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {[
            ["display_name", "Display name"], ["email", "Email"],
            ["first_name", "First name"], ["last_name", "Last name"],
            ["phone", "Phone"], ["headline", "Headline"],
            ["company", "Company"], ["industry", "Industry"],
            ["location", "Location"], ["linkedin", "LinkedIn"],
          ].map(([k, label]) => (
            <div key={k}>
              <div className="text-[11px] uppercase tracking-wider text-[#0d2a20]/60 mb-1">{label}</div>
              <input value={f[k] || ""} onChange={set(k)} className="w-full px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
            </div>
          ))}
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-wider text-[#0d2a20]/60 mb-1">Bio</div>
            <textarea rows={3} value={f.bio || ""} onChange={set('bio')} className="w-full px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
          </div>
          <label className="inline-flex items-center gap-2 text-[13px]"><input type="checkbox" checked={!!f.searchable} onChange={set('searchable')} /> Visible in Member Directory</label>
        </div>
        <div className="mt-5 flex items-center gap-2">
          <button type="submit" disabled={saving} data-testid="member-edit-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px] disabled:opacity-60"><Save size={14} /> {saving ? "Saving…" : "Save"}</button>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md btn-ghost text-[13px]">Cancel</button>
        </div>
      </form>
    </div>
  );
};

const CMSMembers = () => {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null); // {action, member}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/members");
      setList(data);
    } catch { toast({ title: "Failed to load members" }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = list.filter(m => {
    if (filter !== "all" && (m.status || 'active') !== filter) return false;
    if (!q.trim()) return true;
    const rx = q.toLowerCase();
    return (m.display_name || "").toLowerCase().includes(rx) || (m.email || "").toLowerCase().includes(rx);
  });

  const suspend = async (id) => {
    try { await adminApi.post(`/admin/members/${id}/suspend`); toast({ title: "Member suspended" }); await load(); }
    catch { toast({ title: "Failed" }); }
  };
  const reactivate = async (id) => {
    try { await adminApi.post(`/admin/members/${id}/reactivate`); toast({ title: "Member reactivated" }); await load(); }
    catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail || "Try again" }); }
  };
  const del = async (id) => {
    try {
      await adminApi.delete(`/admin/members/${id}?confirm=true`);
      toast({ title: "Member permanently deleted" });
      setConfirm(null);
      await load();
    } catch { toast({ title: "Delete failed" }); }
  };
  const saveEdit = async (updates) => {
    try {
      await adminApi.patch(`/admin/members/${editing.id}`, updates);
      toast({ title: "Member updated" });
      setEditing(null); await load();
    } catch (e) { toast({ title: "Save failed", description: e?.response?.data?.detail || "Try again" }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="section-eyebrow text-[#0d2a20]/60 mb-1">Members</div>
          <h2 className="hero-title text-[28px] leading-tight">Manage members</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0d2a20]/40" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or email" className="pl-9 pr-3 py-2 rounded-md bg-white border text-[13px] w-[240px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} data-testid="members-search" />
          </div>
          <select value={filter} onChange={e => setFilter(e.target.value)} className="px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} data-testid="members-filter">
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>
      </div>

      {loading ? <div className="p-10 text-center text-[14px] text-[#0d2a20]/60">Loading…</div> : filtered.length === 0 ? (
        <div className="p-10 text-center text-[14px] text-[#0d2a20]/60 rounded-lg" style={{ background: '#fbf8ef' }}>No members match your filter.</div>
      ) : (
        <div className="rounded-lg overflow-hidden" style={{ background: '#fbf8ef' }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#0d2a20]/60" style={{ background: '#ece5d3' }}>
                  <th className="py-3 px-4">Name</th>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4">Joined</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(m => {
                  const st = m.status || (m.is_active ? 'active' : 'suspended');
                  return (
                    <tr key={m.id} className="border-b last:border-b-0" style={{ borderColor: 'rgba(13,42,32,0.08)' }} data-testid={`member-row-${m.id}`}>
                      <td className="py-3 px-4 font-medium text-[14px]">{m.display_name || '(no name)'}</td>
                      <td className="py-3 px-4 text-[13px] text-[#0d2a20]/75 flex items-center gap-1"><Mail size={11} /> {m.email}</td>
                      <td className="py-3 px-4 text-[12px] text-[#0d2a20]/60">{fmtDate(m.created_at)}</td>
                      <td className="py-3 px-4"><StatusPill status={st} /></td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button onClick={() => setEditing(m)} className="px-2 py-1 rounded btn-ghost text-[12px]" data-testid={`member-edit-${m.id}`}>Edit</button>
                          {st === 'active' && (
                            <button onClick={() => suspend(m.id)} className="px-2 py-1 rounded text-[12px] inline-flex items-center gap-1" style={{ background: '#fde5c7', color: '#8a4a10' }} data-testid={`member-suspend-${m.id}`}><ShieldOff size={12} /> Suspend</button>
                          )}
                          {st === 'suspended' && (
                            <button onClick={() => reactivate(m.id)} className="px-2 py-1 rounded text-[12px] inline-flex items-center gap-1" style={{ background: '#d6ecd6', color: '#154a1c' }} data-testid={`member-reactivate-${m.id}`}><ShieldCheck size={12} /> Reactivate</button>
                          )}
                          {st !== 'deleted' && (
                            <button onClick={() => setConfirm({ action: 'delete', member: m })} className="px-2 py-1 rounded text-[12px] inline-flex items-center gap-1" style={{ background: '#f3d6d1', color: '#7a1f13' }} data-testid={`member-delete-${m.id}`}><Trash2 size={12} /> Delete</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && <EditModal member={editing} onClose={() => setEditing(null)} onSave={saveEdit} />}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(13,42,32,0.5)' }}>
          <div className="rounded-lg p-6 max-w-md w-full" style={{ background: '#fbf8ef' }} data-testid="member-delete-confirm">
            <h3 className="text-[20px] font-medium mb-2">Permanently delete this member?</h3>
            <p className="text-[14px] text-[#0d2a20]/75 mb-4">This will permanently delete <b>{confirm.member.display_name || confirm.member.email}</b>'s account and associated personal data from the Vemoye platform. Aggregate data (event registration counts, poll totals) will be retained anonymously. This action cannot be undone.</p>
            <div className="flex items-center gap-2">
              <button onClick={() => del(confirm.member.id)} data-testid="member-delete-confirm-btn" className="px-4 py-2 rounded-md text-[13px]" style={{ background: '#7a1f13', color: 'white' }}>Yes, delete permanently</button>
              <button onClick={() => setConfirm(null)} className="px-4 py-2 rounded-md btn-ghost text-[13px]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CMSMembers;
